import React, { useState, useMemo } from 'react';
import {
  Container, Row, Col, Card, Table, Badge,
  Spinner, Alert, Nav, Tab
} from 'react-bootstrap';
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { format } from 'date-fns';
import { useGetDeveloperPayoutQuery } from '../../features/apiSlice';
import { CiMoneyBill } from 'react-icons/ci';
import { BiBarChart, BiCalendar, BiTrendingUp } from 'react-icons/bi';
import { MdAccountBalanceWallet } from 'react-icons/md';
import { FiTrendingUp, FiLayers } from 'react-icons/fi';

// ─── Design tokens ────────────────────────────────────────────────────────────
const CURRENCY_PALETTE = {
  GBP: { primary: '#7c3aed', light: '#f5f3ff', text: '#6d28d9' },
  USD: { primary: '#0284c7', light: '#f0f9ff', text: '#0369a1' },
  EUR: { primary: '#d97706', light: '#fffbeb', text: '#b45309' },
};
const FALLBACK_PALETTE = [
  { primary: '#db2777', light: '#fdf2f8', text: '#be185d' },
  { primary: '#16a34a', light: '#f0fdf4', text: '#15803d' },
];

const getCurrencyPalette = (currency, index) =>
  CURRENCY_PALETTE[currency] || FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];

const formatCurrency = (amount, symbol = '£') =>
  `${symbol}${Number(amount || 0).toFixed(2)}`;

// ─── Shared sub-components ─────────────────────────────────────────────────────
const StatPill = ({ label, value, bg = '#f1f5f9', color = '#334155' }) => (
  <div style={{ background: bg, borderRadius: 8, padding: '10px 14px' }}>
    <small style={{ color: '#64748b', display: 'block', marginBottom: 2, fontSize: '0.75rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </small>
    <span style={{ fontWeight: 700, color, fontSize: '0.95rem' }}>{value}</span>
  </div>
);

const SectionHeading = ({ children }) => (
  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 10 }}>
    {children}
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
function DeveloperPay() {
  const { data, isLoading, error } = useGetDeveloperPayoutQuery();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedChartCurrency, setSelectedChartCurrency] = useState(null);

  // ── Destructure with safe fallbacks so hooks below are always called ────────
  const { monthlyBreakdown = {}, currencySummary = {}, summary = {} } = data || {};

  const availableCurrencies = Object.keys(currencySummary);
  const chartCurrency = selectedChartCurrency || availableCurrencies[0] || 'GBP';

  // Months from API are descending; reverse to ascending for charts
  // useMemo MUST be called here — before any early return — to satisfy Rules of Hooks
  const chartMonths = useMemo(
    () => [...Object.keys(monthlyBreakdown)].reverse(),
    [monthlyBreakdown]
  );

  // Subdomain performance — built in ascending order (for charts)
  const subdomainPerformance = useMemo(() => {
    const perf = {};
    chartMonths.forEach(month => {
      Object.entries(monthlyBreakdown[month]?.bySubdomain || {}).forEach(([subdomain, sd]) => {
        if (!perf[subdomain]) {
          perf[subdomain] = {
            totalRevenue: 0,
            totalCommission: 0,
            threshold: sd.threshold,
            currency: sd.currency,
            months: [],
            revenueHistory: [],
            commissionHistory: [],
          };
        }
        perf[subdomain].totalRevenue += sd.revenue;
        perf[subdomain].totalCommission += sd.commission;
        perf[subdomain].months.push(format(new Date(month), 'MMM yy'));
        perf[subdomain].revenueHistory.push(sd.revenue);
        perf[subdomain].commissionHistory.push(sd.commission);
      });
    });
    return perf;
  }, [chartMonths, monthlyBreakdown]);

  // ── Early returns — placed AFTER all hooks to satisfy Rules of Hooks ────────
  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" style={{ color: '#7c3aed' }} />
        <span className="ms-3 text-muted">Loading payout data…</span>
      </Container>
    );
  }
  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p className="mb-0">Failed to fetch developer payout information. Please try again later.</p>
        </Alert>
      </Container>
    );
  }
  if (!data) {
    return (
      <Container className="mt-4">
        <Alert variant="info">No payout data available.</Alert>
      </Container>
    );
  }

  // ── Derived chart values (safe to compute now that data is guaranteed) ──────
  const chartMonthLabels = chartMonths.map(m => format(new Date(m), 'MMM yy'));
  const revenueChartData = chartMonths.map(
    m => monthlyBreakdown[m]?.totalRevenue?.[chartCurrency] || 0
  );
  const commissionChartData = chartMonths.map(
    m => monthlyBreakdown[m]?.totalCommission?.[chartCurrency] || 0
  );
  const currencyPieData = availableCurrencies.map((currency, index) => ({
    id: index,
    value: currencySummary[currency].totalCommission,
    label: currency,
    color: getCurrencyPalette(currency, index).primary,
  }));

  // ── Shared chart axis style ─────────────────────────────────────────────────
  const axisStyle = { fontSize: 11, fill: '#94a3b8' };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh'}}>
      {/* <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" /> */}

      <Container fluid className="py-4">

        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(130deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
          borderRadius: 16, padding: '28px 32px', marginBottom: 28,
          boxShadow: '0 8px 32px rgba(99,102,241,0.25)'
        }}>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div style={{
                background: 'rgba(255,255,255,0.12)', borderRadius: 12,
                width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <MdAccountBalanceWallet size={26} color="white" />
              </div>
              <div>
                <h4 style={{ color: 'white', fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>
                  Developer Payout Dashboard
                </h4>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.87rem' }}>
                  {format(new Date(summary.startDate), 'MMMM yyyy')} · onwards
                </p>
              </div>
            </div>
            <div className="d-flex gap-3 flex-wrap">
              {[
                { icon: <BiCalendar size={16} />, label: 'Months', value: summary.monthsProcessed },
                // { icon: <FiLayers size={16} />, label: 'Subdomains', value: summary.subdomainsWithThreshold },
              ].map(({ icon, label, value }) => (
                <div key={label} style={{
                  background: 'rgba(255,255,255,0.1)', borderRadius: 10,
                  padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.6)' }}>{icon}</span>
                  <div>
                    <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                    <div style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Per-currency summary cards ── */}
        {/* FIX: was showing combined cross-currency totalRevenue/totalCommission — now one card per currency */}
        <Row className="mb-4 g-3">
          {availableCurrencies.map((currency, index) => {
            const cData = currencySummary[currency];
            const palette = getCurrencyPalette(currency, index);
            // FIX: averageMonthlyCommission is now per-currency
            const avgCommission =
              summary.averageMonthlyCommissionByCurrency?.[currency]?.averageMonthlyCommission ?? 0;

            return (
              <Col md={Math.floor(12 / (availableCurrencies.length + 1))} key={currency}>
                <Card className="border-0 h-100" style={{
                  borderRadius: 14, overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
                }}>
                  <div style={{ height: 4, background: palette.primary }} />
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <span style={{
                          background: palette.light, color: palette.text,
                          fontWeight: 700, fontSize: '0.78rem', padding: '3px 10px',
                          borderRadius: 20, letterSpacing: '0.04em'
                        }}>
                          {currency} · {cData.symbol}
                        </span>
                      </div>
                      <CiMoneyBill size={22} style={{ color: palette.primary, opacity: 0.7 }} />
                    </div>

                    <div className="mb-3">
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                        Total Revenue
                      </div>
                      <div style={{  fontWeight: 500, fontSize: '1.35rem', color: '#1e293b' }}>
                        {formatCurrency(cData.totalRevenue, cData.symbol)}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                        Total Commission
                      </div>
                      <div style={{  fontWeight: 600, fontSize: '1.35rem', color: palette.primary }}>
                        {formatCurrency(cData.totalCommission, cData.symbol)}
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <small style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Avg / month</small>
                      <span style={{  fontWeight: 500, color: '#475569', fontSize: '0.88rem' }}>
                        {formatCurrency(avgCommission, cData.symbol)}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}

          {/* Months processed card */}
          <Col md={Math.floor(12 / (availableCurrencies.length + 1))}>
            <Card className="border-0 h-100" style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
              <div style={{ height: 4, background: '#0ea5e9' }} />
              <Card.Body className="p-4 d-flex flex-column justify-content-center">
                <BiCalendar size={26} style={{ color: '#0ea5e9', marginBottom: 12, opacity: 0.8 }} />
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                  Months Active
                </div>
                <div style={{  fontWeight: 700, fontSize: '2rem', color: '#1e293b', lineHeight: 1 }}>
                  {summary.monthsProcessed}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ── Tabs ── */}
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          {/* Tab nav */}
          <div style={{ marginBottom: 20 }}>
            <Nav style={{ gap: 4 }}>
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'monthly', label: 'Monthly Breakdown' },
                { key: 'subdomains', label: 'Subdomain Performance' },
              ].map(({ key, label }) => (
                <Nav.Item key={key}>
                  <Nav.Link
                    eventKey={key}
                    style={{
                      borderRadius: 8, padding: '8px 18px', fontWeight: 600,
                      fontSize: '0.875rem', border: 'none',
                      background: activeTab === key ? '#312e81' : 'white',
                      color: activeTab === key ? 'white' : '#64748b',
                      boxShadow: activeTab === key ? '0 2px 8px rgba(49,46,129,0.25)' : '0 1px 3px rgba(0,0,0,0.07)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>

          <Tab.Content>
            {/* ════════════════ OVERVIEW TAB ════════════════ */}
            <Tab.Pane eventKey="overview">
              {/* Currency selector for charts */}
              <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
                <small style={{ color: '#94a3b8', fontWeight: 500 }}>Chart currency:</small>
                {availableCurrencies.map((cur, i) => {
                  const p = getCurrencyPalette(cur, i);
                  const active = chartCurrency === cur;
                  return (
                    <button
                      key={cur}
                      onClick={() => setSelectedChartCurrency(cur)}
                      style={{
                        background: active ? p.primary : p.light,
                        color: active ? 'white' : p.text,
                        border: 'none', borderRadius: 6, padding: '5px 16px',
                        fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                        transition: 'all 0.15s',
                        boxShadow: active ? `0 2px 8px ${p.primary}55` : 'none',
                      }}
                    >
                      {cur}
                    </button>
                  );
                })}
              </div>

              <Row className="g-3 mb-3">
                {/* Bar chart */}
                <Col lg={8}>
                  <Card className="border-0 h-100" style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                    <Card.Body className="p-4">
                      <SectionHeading>Monthly Revenue vs Commission · {chartCurrency}</SectionHeading>
                      <BarChart
                        xAxis={[{
                          scaleType: 'band', data: chartMonthLabels,
                          tickLabelStyle: axisStyle,
                        }]}
                        series={[
                          { data: revenueChartData, label: 'Revenue', color: getCurrencyPalette(chartCurrency, availableCurrencies.indexOf(chartCurrency)).primary },
                          { data: commissionChartData, label: 'Commission', color: '#22c55e' },
                        ]}
                        height={340}
                        margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
                      />
                    </Card.Body>
                  </Card>
                </Col>

                {/* Pie chart */}
                <Col lg={4}>
                  <Card className="border-0 h-100" style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                    <Card.Body className="p-4">
                      <SectionHeading>Commission by Currency</SectionHeading>
                      <PieChart
                        series={[{
                          data: currencyPieData,
                          innerRadius: 45,
                          outerRadius: 90,
                          paddingAngle: 3,
                          cornerRadius: 6,
                          highlightScope: { fade: 'global', highlight: 'item' },
                        }]}
                        height={220}
                      />
                      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {availableCurrencies.map((currency, i) => {
                          const cData = currencySummary[currency];
                          const palette = getCurrencyPalette(currency, i);
                          return (
                            <div key={currency} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: palette.light, borderRadius: 8 }}>
                              <div className="d-flex align-items-center gap-2">
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: palette.primary, flexShrink: 0 }} />
                                <span style={{ fontWeight: 600, color: palette.text, fontSize: '0.85rem' }}>{currency}</span>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: 900, color: palette.primary, fontSize: '0.9rem' }}>
                                  {formatCurrency(cData.totalCommission, cData.symbol)}
                                </div>
                                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                  {formatCurrency(cData.totalRevenue, cData.symbol)} rev
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Commission trend line */}
              <Card className="border-0" style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                <Card.Body className="p-4">
                  <SectionHeading>Commission Trend · {chartCurrency}</SectionHeading>
                  <LineChart
                    xAxis={[{ scaleType: 'band', data: chartMonthLabels, tickLabelStyle: axisStyle }]}
                    series={[{
                      data: commissionChartData,
                      label: 'Commission',
                      color: getCurrencyPalette(chartCurrency, availableCurrencies.indexOf(chartCurrency)).primary,
                      showMark: true,
                      area: true,
                    }]}
                    height={240}
                    margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
                  />
                </Card.Body>
              </Card>
            </Tab.Pane>

            {/* ════════════════ MONTHLY BREAKDOWN TAB ════════════════ */}
            {/* FIX: monthData.totalRevenue and .totalCommission are now objects { GBP: n, ... } */}
            <Tab.Pane eventKey="monthly">
              <Row className="g-3">
                {Object.entries(monthlyBreakdown).map(([month, monthData]) => {
                  const monthCurrencies = Object.keys(monthData.byCurrency);

                  return (
                    <Col lg={6} key={month}>
                      <Card className="border-0 h-100" style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                        <Card.Body className="p-4">
                          {/* Month header */}
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 style={{ fontWeight: 700, margin: 0, color: '#1e293b', fontSize: '1rem' }}>{month}</h6>
                            <div className="d-flex gap-1 flex-wrap justify-content-end">
                              {monthCurrencies.map((cur, i) => {
                                const palette = getCurrencyPalette(cur, i);
                                const sym = currencySummary[cur]?.symbol || cur;
                                // FIX: indexing per-currency object instead of treating as a number
                                const commission = monthData.totalCommission[cur] || 0;
                                return (
                                  <span key={cur} style={{
                                    background: palette.light, color: palette.text,
                                    fontSize: '0.75rem', fontWeight: 600,
                                    padding: '3px 9px', borderRadius: 20,
                                  }}>
                                    {cur}: {formatCurrency(commission, sym)}
                                  </span>
                                );
                              })}
                            </div>
                          </div>

                          {/* Per-currency stat pills */}
                          <Row className="g-2 mb-3">
                            {monthCurrencies.map((cur, i) => {
                              const palette = getCurrencyPalette(cur, i);
                              const sym = currencySummary[cur]?.symbol || cur;
                              // FIX: .totalRevenue[cur] not .totalRevenue (which was a mixed-currency number)
                              const revenue    = monthData.totalRevenue[cur]    || 0;
                              const commission = monthData.totalCommission[cur] || 0;
                              const rate = revenue > 0 ? ((commission / revenue) * 100).toFixed(1) : '0.0';

                              return (
                                <Col xs={12 / monthCurrencies.length} key={cur}>
                                  <div style={{
                                    background: palette.light, borderRadius: 10,
                                    padding: '10px 12px',
                                    borderLeft: `3px solid ${palette.primary}`
                                  }}>
                                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: palette.text, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                                      {cur}
                                    </div>
                                    {[
                                      { label: 'Revenue', value: formatCurrency(revenue, sym) },
                                      { label: 'Commission', value: formatCurrency(commission, sym) },
                                      { label: 'Rate', value: `${rate}%` },
                                    ].map(({ label, value }) => (
                                      <div key={label} className="d-flex justify-content-between">
                                        <small style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{label}</small>
                                        <small style={{  fontWeight: 600, color: '#334155', fontSize: '0.75rem' }}>{value}</small>
                                      </div>
                                    ))}
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>

                          {/* Subdomain table */}
                          <SectionHeading>Subdomain Breakdown</SectionHeading>
                          <Table size="sm" className="mb-0" style={{ fontSize: '0.83rem' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                {['Subdomain', 'Revenue', 'Commission', 'Threshold'].map(h => (
                                  <th key={h} style={{ fontWeight: 600, color: '#94a3b8', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 8px', background: 'transparent', border: 'none' }}
                                    className={h !== 'Subdomain' ? 'text-end' : ''}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(monthData.bySubdomain).map(([name, sd]) => {
                                const sym = currencySummary[sd.currency]?.symbol || sd.currency;
                                return (
                                  <tr key={name} style={{ borderBottom: '1px solid #f8fafc' }}>
                                    <td style={{ padding: '7px 8px', fontWeight: 600, color: '#334155' }}>{name}</td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right',  color: '#475569' }}>
                                      {formatCurrency(sd.revenue, sym)}
                                    </td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right',  fontWeight: 600, color: sd.commission > 0 ? '#16a34a' : '#94a3b8' }}>
                                      {formatCurrency(sd.commission, sym)}
                                    </td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                                      {sd.threshold > 0
                                        ? <span style={{  color: '#94a3b8', fontSize: '0.8rem' }}>{formatCurrency(sd.threshold, sym)}</span>
                                        : <span style={{ background: '#dcfce7', color: '#16a34a', fontSize: '0.7rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20 }}>None</span>
                                      }
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Tab.Pane>

            {/* ════════════════ SUBDOMAIN PERFORMANCE TAB ════════════════ */}
            <Tab.Pane eventKey="subdomains">
              <Row className="g-3">
                {Object.entries(subdomainPerformance).map(([name, sd]) => {
                  const palette = getCurrencyPalette(sd.currency, 0);
                  const sym = currencySummary[sd.currency]?.symbol || sd.currency;
                  const commissionRate = sd.totalRevenue > 0
                    ? ((sd.totalCommission / sd.totalRevenue) * 100).toFixed(1)
                    : '0.0';

                  return (
                    <Col lg={6} key={name}>
                      <Card className="border-0" style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                        <div style={{ height: 4, background: palette.primary }} />
                        <Card.Body className="p-4">
                          {/* Card header */}
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <h6 style={{ fontWeight: 700, margin: 0, color: '#1e293b' }}>{name}</h6>
                            <span style={{
                              background: sd.threshold === 0 ? '#dcfce7' : '#f1f5f9',
                              color: sd.threshold === 0 ? '#16a34a' : '#64748b',
                              fontSize: '0.75rem', fontWeight: 600,
                              padding: '3px 10px', borderRadius: 20,
                            }}>
                              {sd.threshold > 0 ? `Threshold: ${formatCurrency(sd.threshold, sym)}` : 'No Threshold'}
                            </span>
                          </div>

                          {/* Stat pills */}
                          <Row className="g-2 mb-3">
                            <Col xs={4}>
                              <StatPill label="Total Revenue" value={formatCurrency(sd.totalRevenue, sym)} bg="#f8fafc" color="#334155" />
                            </Col>
                            <Col xs={4}>
                              <StatPill label="Commission" value={formatCurrency(sd.totalCommission, sym)} bg="#f0fdf4" color="#16a34a" />
                            </Col>
                            <Col xs={4}>
                              <StatPill label="Rate" value={`${commissionRate}%`} bg={palette.light} color={palette.text} />
                            </Col>
                          </Row>

                          {/* Monthly mini chart */}
                          <SectionHeading>Monthly Performance</SectionHeading>
                          <LineChart
                            xAxis={[{ scaleType: 'band', data: sd.months, tickLabelStyle: { ...axisStyle, fontSize: 10 } }]}
                            series={[
                              { data: sd.revenueHistory, label: 'Revenue', color: palette.primary, showMark: true },
                              { data: sd.commissionHistory, label: 'Commission', color: '#22c55e', showMark: true },
                            ]}
                            height={190}
                            margin={{ top: 16, right: 16, bottom: 36, left: 50 }}
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>
    </div>
  );
}

export default DeveloperPay;